import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Stack,
  TextField,
  Grid,
  Avatar,
  Alert,
  Chip,
  Fade,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import apiAxiosInstance, { aiAxiosInstance } from '../../utils/axiosInstance';
import RosterPanel from './RosterPanel';
import SessionPanel from './SessionPanel';
import FormattedMessage from '../Behavior/Chat/FormattedMessage';
import SessionPlannerDialog from './Dialogs/SessionPlannerDialog';
import AddStudentDialog from '../Behavior/Dialogs/AddStudentDialog';
import SectionEmptyState from '../Common/SectionEmptyState';
import { INSTRUCTION_STARTER_PROMPTS } from '../../constants/emptyStatePrompts';
import type {
  InstructionCoachContext,
  InstructionNote,
  InstructionSession,
} from '../../Types';
import { buildAiAuthHeaders, consumeSSEStream } from '../../hooks/useSSEStream';

interface ConversationSummary {
  date: string;
  summary: string;
  keyPoints: string[];
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

const InstructionPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<InstructionSession[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiReply, setAiReply] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedScope, setSelectedScope] = useState<'student' | 'group' | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string>('');
  const [ctx, setCtx] = useState<InstructionCoachContext>({});
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rosterRefreshTrigger, setRosterRefreshTrigger] = useState(0);
  
  // Conversational mode - enabled by default
  const [conversationalMode, setConversationalMode] = useState(true);
  
  // Conversation summaries for progress tracking
  const [conversationSummaries, setConversationSummaries] = useState<ConversationSummary[]>([]);
  
  // Track if we need to show contextual greeting
  const [hasShownGreeting, setHasShownGreeting] = useState(false);
  
  // Track if we're currently loading a session
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, aiReply]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        setLoadError(null);
        const res = await apiAxiosInstance.get('/api/instruction/sessions');
        setSessions(res.data || []);
      } catch (e: any) {
        console.error('Failed to load sessions', e);
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Could not load instruction sessions. Please try again.';
        console.error('Error details:', e?.response?.data || e);
        setLoadError(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load conversation data when session changes
  useEffect(() => {
    const loadSessionData = async () => {
      if (!ctx.session?._id) {
        setConversationSummaries([]);
        setMessages([]);
        setHasShownGreeting(false);
        return;
      }
      
      setIsLoadingSession(true);
      try {
        // Load conversation history first
        const loadedMessages = await loadChatHistory(ctx.session._id);
        const summaries = await loadConversationSummaries(ctx.session._id);
        
        // If we have chat history, use it
        if (loadedMessages.length > 0) {
          setMessages(loadedMessages);
          setHasShownGreeting(true);
        } else if (!hasShownGreeting) {
          // Otherwise show greeting
          await showContextualGreeting(summaries);
          setHasShownGreeting(true);
        }
      } catch (e: any) {
        console.error('Failed to load session data', e);
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Could not load conversation history for this session.';
        console.error('Error details:', e?.response?.data || e);
        setLoadError(errorMsg);
      } finally {
        setIsLoadingSession(false);
      }
    };
    
    loadSessionData();
  }, [ctx.session?._id]); // Only run when session changes

  const loadChatHistory = async (sessionId: string): Promise<ChatMessage[]> => {
    try {
      console.log('[Instruction] Loading chat history for session:', sessionId);
      
      const res = await apiAxiosInstance.get(`/api/instruction/sessions/${sessionId}/notes`);
      const notes: InstructionNote[] = res.data || [];

      const chatMessages: ChatMessage[] = notes
        .filter((n) => n.type === 'chat_message')
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((n) => ({
          role: (n.senderRole === 'user' ? 'user' : 'assistant') as ChatMessage['role'],
          content: n.content || '',
          timestamp: n.createdAt,
        }));
      
      console.log('[Instruction] Loaded chat messages:', chatMessages.length);
      return chatMessages;
    } catch (e) {
      console.error('[Instruction] Failed to load chat history', e);
      return [];
    }
  };

  const loadConversationSummaries = async (sessionId: string): Promise<ConversationSummary[]> => {
    try {
      console.log('[Instruction] Loading conversation summaries for session:', sessionId);
      
      const res = await apiAxiosInstance.get(`/api/instruction/sessions/${sessionId}/notes`);
      const notes: InstructionNote[] = res.data || [];

      const summaries: ConversationSummary[] = notes
        .filter((n) => n.type === 'conversation_summary' || n.type === 'ai_summary')
        .map((n) => ({
          date: n.createdAt,
          summary: n.content || '',
          keyPoints: n.keyPoints || [],
        }))
        .slice(0, 10);
      
      console.log('[Instruction] Parsed summaries:', summaries.length);
      setConversationSummaries(summaries);
      return summaries;
    } catch (e) {
      console.error('[Instruction] Failed to load conversation summaries', e);
      return [];
    }
  };

  const showContextualGreeting = async (summaries: ConversationSummary[]) => {
    if (!ctx.session) return;
    
    const hasPreviousConversation = summaries.length > 0;
    const lastInteraction = summaries[0];
    const focusSkill = ctx.session.focusSkill || 'this skill';
    
    let greetingContent = '';
    
    if (hasPreviousConversation && lastInteraction) {
      const dateStr = new Date(lastInteraction.date).toLocaleDateString();
      greetingContent = `Welcome back! Last time we talked on ${dateStr}, we discussed: "${lastInteraction.summary.substring(0, 80)}..." What would you like to work on today for ${focusSkill}?`;
    } else {
      greetingContent = `Hi! I'm here to help you plan and deliver targeted instruction${focusSkill !== 'this skill' ? ` for ${focusSkill}` : ''}. What would you like to focus on today with ${selectedName}?`;
    }
    
    // Save greeting as first message
    if (ctx.session._id) {
      await saveChatMessage(ctx.session._id, 'assistant', greetingContent);
    }
    
    setMessages([{ role: 'assistant', content: greetingContent }]);
  };

  const saveChatMessage = async (sessionId: string, role: 'user' | 'assistant', content: string) => {
    try {
      await apiAxiosInstance.post(`/api/instruction/sessions/${sessionId}/notes`, {
        type: 'chat_message',
        senderRole: role,
        content: content,
      });
      console.log('[Instruction] Saved chat message:', role, content.substring(0, 50));
    } catch (e: any) {
      console.error('[Instruction] Failed to save chat message', e);
      const errorMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Message could not be saved';
      console.error('Error details:', e?.response?.data || e);
      setError(errorMsg + '. Your reply may not appear after refresh.');
    }
  };

  const handleSelect = (scope: 'student' | 'group', id: string, name: string) => {
    setSelectedScope(scope);
    setSelectedId(id);
    setSelectedName(name);
    setMessages([]);
    setHasShownGreeting(false);
    setError(null);
  };

  const generateConversationSummary = useCallback(async (userMessage: string, aiResponse: string) => {
    if (!ctx.session?._id) return;
    
    try {
      const response = await fetch(`${aiAxiosInstance.defaults.baseURL}/instruction/coach/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userMessage,
          aiResponse,
          previousSummaries: conversationSummaries.slice(0, 3),
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        await apiAxiosInstance.post(`/api/instruction/sessions/${ctx.session._id}/notes`, {
          type: 'conversation_summary',
          content: data.summary,
          keyPoints: data.keyPoints,
        });
        
        const newSummary: ConversationSummary = {
          date: new Date().toISOString(),
          summary: data.summary,
          keyPoints: data.keyPoints,
        };
        setConversationSummaries(prev => [newSummary, ...prev]);
      }
    } catch (e: any) {
      console.error('[Instruction] Failed to generate conversation summary', e);
      const errorMsg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to generate conversation summary';
      console.error('Error details:', e?.response?.data || e);
    }
  }, [ctx.session?._id, conversationSummaries]);

  const handleSendMessage = async () => {
    if (!aiInput.trim()) return;
    
    if (!selectedId) {
      setError('Please select a student or group from the roster first');
      return;
    }

    const userContent = aiInput.trim();
    const userMsg: ChatMessage = { role: 'user', content: userContent };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setAiInput('');
    setAiReply('');
    setError(null);

    // Save user message
    if (ctx.session?._id) {
      await saveChatMessage(ctx.session._id, 'user', userContent);
    }

    try {
      let full = '';
      let aiSummary: string | null = null;

      await consumeSSEStream({
        url: `${aiAxiosInstance.defaults.baseURL}/instruction/coach/stream`,
        headers: buildAiAuthHeaders(),
        body: JSON.stringify({
          message: userContent,
          scope: selectedScope,
          session: ctx.session || null,
          progress: ctx.progress || null,
          notes: ctx.notes || [],
          messages: updatedMessages,
          conversationalMode,
          conversationSummaries: conversationSummaries.slice(0, 5),
        }),
        onEvent: (data) => {
          if (typeof data.content === 'string') {
            full += data.content;
            setAiReply((prev) => prev + data.content);
          }
          if (
            data.event === 'done' &&
            typeof data.ai_summary === 'string' &&
            data.ai_summary.trim()
          ) {
            aiSummary = data.ai_summary.trim();
          }
        },
      });

      // Save AI response
      if (ctx.session?._id) {
        await saveChatMessage(ctx.session._id, 'assistant', full);
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: full }]);
      setAiReply('');

      // Save AI summary as note
      if (aiSummary && ctx.session) {
        try {
          await apiAxiosInstance.post(`/api/instruction/sessions/${ctx.session._id}/notes`, {
            type: 'ai_summary',
            content: aiSummary,
          });
        } catch {}
      }
      
      // Generate conversation summary for progress tracking
      if (conversationalMode) {
        await generateConversationSummary(userContent, full);
      }
    } catch (e: any) {
      setError(e?.message || 'Error sending message');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ p: 2, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Targeted Instruction
        {selectedName && (
          <Chip
            label={selectedName}
            color="primary"
            size="small"
            sx={{ ml: 1 }}
            icon={selectedScope === 'student' ? <PersonIcon /> : <SchoolIcon />}
          />
        )}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Sidebar - Roster */}
        <Grid item xs={12} md={3} sx={{ height: '100%' }}>
          <RosterPanel
            onSelect={handleSelect}
            selectedId={selectedId}
            selectedScope={selectedScope}
            onAddStudent={() => setAddStudentOpen(true)}
            onAddGroup={() => setPlannerOpen(true)}
            refreshTrigger={rosterRefreshTrigger}
          />
        </Grid>

        {/* Main Chat Area */}
        <Grid item xs={12} md={6} sx={{ height: '100%' }}>
          <Paper
            sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            {/* Chat Mode Toggle */}
            {selectedId && (
              <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conversationalMode}
                      onChange={(e) => setConversationalMode(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ChatIcon fontSize="small" color={conversationalMode ? 'primary' : 'disabled'} />
                      <Typography variant="caption">
                        {conversationalMode ? 'Coach Mode - Direct, practical responses' : 'Detailed Mode - Comprehensive responses'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {/* Messages Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loadError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
                  {loadError}
                </Alert>
              )}

              {!selectedId && (
                <SectionEmptyState
                  icon={<PersonIcon sx={{ fontSize: 48 }} />}
                  title="Select a student or group"
                  description="Choose from the roster on the left, then create or open an instruction session on the right."
                />
              )}

              {selectedId && !ctx.session && !isLoadingSession && (
                <SectionEmptyState
                  icon={<SchoolIcon sx={{ fontSize: 48 }} />}
                  title="Open or create a session"
                  description={`Start a session for ${selectedName} in the panel on the right, or plan one with the session planner.`}
                  actionLabel="Plan session"
                  onAction={() => setPlannerOpen(true)}
                />
              )}

              {isLoadingSession && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Loading conversation history...
                </Alert>
              )}

              {ctx.session && messages.length === 0 && !isLoadingSession && !aiReply && (
                <SectionEmptyState
                  icon={<SchoolIcon sx={{ fontSize: 48 }} />}
                  title="Targeted Instruction"
                  description={`Plan lessons, checks for understanding, and differentiation for ${selectedName}.`}
                  prompts={INSTRUCTION_STARTER_PROMPTS.map((label) => ({
                    label,
                    onClick: () => setAiInput(label),
                  }))}
                />
              )}

              {messages.map((m, idx) => (
                <Box
                  key={idx}
                  sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}
                >
                  <Avatar
                    sx={{
                      bgcolor: m.role === 'assistant' ? 'primary.main' : 'secondary.main',
                      mr: 2,
                      width: 32,
                      height: 32,
                      fontSize: 14,
                    }}
                  >
                    {m.role === 'assistant' ? <AIIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <FormattedMessage content={m.content} role={m.role} />
                  </Box>
                </Box>
              ))}

              {aiReply && (
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      mr: 2,
                      width: 32,
                      height: 32,
                      fontSize: 14,
                    }}
                  >
                    <AIIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <FormattedMessage content={aiReply} role="assistant" />
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          animation: 'pulse 1s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  placeholder={
                    selectedId
                      ? conversationalMode 
                        ? "Ask about lesson planning, teaching strategies, or student support..."
                        : "Ask detailed questions about instruction..."
                      : "Select a student/group first..."
                  }
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!selectedId || isLoadingSession}
                  multiline
                  maxRows={3}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!selectedId || !aiInput.trim() || isLoadingSession}
                  startIcon={<SendIcon />}
                  sx={{ minWidth: 100 }}
                >
                  Send
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Right Sidebar - Session Details */}
        <Grid item xs={12} md={3} sx={{ height: '100%' }}>
          <SessionPanel 
            scope={selectedScope} 
            id={selectedId} 
            name={selectedName}
            onLoadContext={setCtx}
            onSessionCreated={() => setRosterRefreshTrigger(prev => prev + 1)}
          />
        </Grid>
      </Grid>

      {/* Session Planner Dialog */}
      <SessionPlannerDialog
        open={plannerOpen}
        onClose={() => {
          setPlannerOpen(false);
          setRosterRefreshTrigger(prev => prev + 1);
        }}
        onSessionCreated={(session) => {
          setRosterRefreshTrigger(prev => prev + 1);
        }}
        preselectedScope={selectedScope}
        preselectedId={selectedId}
        preselectedName={selectedName}
      />

      {/* Add Student Dialog */}
      <AddStudentDialog
        open={addStudentOpen}
        onClose={() => {
          setAddStudentOpen(false);
          setRosterRefreshTrigger(prev => prev + 1);
        }}
        onStudentAdded={(student) => {
          setRosterRefreshTrigger(prev => prev + 1);
        }}
      />
    </Box>
  );
};

export default InstructionPage;
